import type { GallerySnapshot } from "@roboscientist/contracts";
import { parseGallerySnapshot, sortGallerySnapshots } from "./snapshot.js";
import { normalizeGalleryMessage } from "./storageMetadata.js";
import { publicReadOnly, publishedAssetUrl } from "./publicGallery.js";

export type GallerySnapshotIndex = {
  snapshots: GallerySnapshot[];
  messages: Record<string, string>;
  storagePath: string;
};

const record = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;

const responseError = async (response: Response): Promise<Error> => {
  try {
    const payload = await response.json() as { error?: unknown };
    if (typeof payload.error === "string" && payload.error) return new Error(payload.error);
  } catch {
    // Fall back to the HTTP status below.
  }
  return new Error(`Gallery request failed with HTTP ${response.status}.`);
};

export async function listGallerySnapshots(): Promise<GallerySnapshotIndex> {
  const response = await fetch("./api/v1/gallery/snapshots", { cache: "no-store" });
  if (!response.ok) throw await responseError(response);
  const payload = await response.json() as { snapshots?: unknown; messages?: unknown; storagePath?: unknown };
  if (!Array.isArray(payload.snapshots) || typeof payload.storagePath !== "string") {
    throw new Error("Gallery server returned an invalid snapshot index.");
  }
  const snapshots = sortGallerySnapshots(payload.snapshots.map(parseGallerySnapshot));
  const rawMessages = record(payload.messages);
  return {
    snapshots,
    messages: Object.fromEntries(snapshots.map((snapshot) => {
      const value = rawMessages?.[snapshot.snapshot.id];
      return [snapshot.snapshot.id, typeof value === "string" ? normalizeGalleryMessage(value) : ""];
    })),
    storagePath: payload.storagePath
  };
}

export async function storeGallerySnapshot(file: File, message = ""): Promise<GallerySnapshot> {
  const normalizedMessage = normalizeGalleryMessage(message);
  const headers: Record<string, string> = {
    "Content-Type": "application/octet-stream",
    "X-Gallery-Filename": encodeURIComponent(file.name)
  };
  if (normalizedMessage) headers["X-Gallery-Message"] = encodeURIComponent(normalizedMessage);
  const response = await fetch("./api/v1/gallery/snapshots", {
    method: "POST",
    headers,
    body: file
  });
  if (!response.ok) throw await responseError(response);
  const payload = await response.json() as { snapshot?: unknown };
  return parseGallerySnapshot(payload.snapshot);
}

export async function updateGallerySnapshotMessage(snapshotId: string, message: string): Promise<string> {
  const query = new URLSearchParams({ id: snapshotId });
  const response = await fetch(`./api/v1/gallery/snapshots?${query}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message: normalizeGalleryMessage(message) })
  });
  if (!response.ok) throw await responseError(response);
  const payload = await response.json() as { message?: unknown };
  return normalizeGalleryMessage(payload.message);
}

export async function deleteGallerySnapshot(snapshotId: string): Promise<void> {
  const query = new URLSearchParams({ id: snapshotId });
  const response = await fetch(`./api/v1/gallery/snapshots?${query}`, { method: "DELETE" });
  if (!response.ok) throw await responseError(response);
}

export async function gallerySnapshotAsset(snapshotId: string, assetPath: string): Promise<Blob | null> {
  if (publicReadOnly) {
    const url = publishedAssetUrl(snapshotId, assetPath);
    if (!url) return null;
    const response = await fetch(url);
    if (response.status === 404) return null;
    if (!response.ok) throw await responseError(response);
    return response.blob();
  }
  const query = new URLSearchParams({ snapshotId, path: assetPath });
  const response = await fetch(`./api/v1/gallery/assets?${query}`);
  if (response.status === 404) return null;
  if (!response.ok) throw await responseError(response);
  return response.blob();
}
